import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../backend/firebaseConfig"; // adjust
import ButtonSmall from "../../DesignSystem/Buttons/ButtonSmall";
import Banner from "../../DesignSystem/Banner/Banner";
import { writeCheck2Announcement } from "./writeAnnouncement";
import "../../../css/Compliance/AML/Check2AnnouncementAdmin.css";
import dayjs from "dayjs";
import { uploadCheck2Guide } from "./uploadCheck2Guide";

const clean = (v) => (v ?? "").toString().trim();

const TYPE_OPTIONS = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "success", label: "Success" },
  { value: "error", label: "Error" },
];

export default function Check2AnnouncementAdmin() {
  const [loading, setLoading] = useState(true);

  // live values from DB
  const [dbMessage, setDbMessage] = useState("");
  const [dbEnabled, setDbEnabled] = useState(false);
  const [dbType, setDbType] = useState("warning");
  const [dbUpdatedBy, setDbUpdatedBy] = useState("");
  const [dbUpdatedOn, setDbUpdatedOn] = useState("");

  // draft values (editable)
  const [message, setMessage] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [type, setType] = useState("warning");

  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ kind: "", text: "" });
  const [dbGuideUrl, setDbGuideUrl] = useState("");
  const [guideUrl, setGuideUrl] = useState(""); 
  const [selectedGuideFile, setSelectedGuideFile] = useState(null);
  const [uploadingGuide, setUploadingGuide] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);


  const hasChanges = useMemo(() => {
    return (
      clean(message) !== clean(dbMessage) ||
      !!enabled !== !!dbEnabled ||
      clean(type) !== clean(dbType)
    );
  }, [message, enabled, type, dbMessage, dbEnabled, dbType]);

  useEffect(() => {
    const ref = doc(db, "Compliance", "Check²");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const msg = clean(data?.announcement);
        const isEnabled = !!data?.enabled;
        const t = clean(data?.announcementType || "warning").toLowerCase();
        const updatedBy = clean(data?.updatedBy || "");
        const updatedAt = data?.updatedAt
          ? new dayjs(data?.updatedAt?.toDate()).format("DD MMM YYYY")
          : "";
        const guide = clean(data?.guide || "");
        setDbGuideUrl(guide);
        setGuideUrl(guide); 
        setDbUpdatedBy(updatedBy);
        setDbUpdatedOn(updatedAt);
        setDbMessage(msg);
        setDbEnabled(isEnabled);
        setDbType(t || "warning");

        // If user hasn't started editing, sync draft to DB
        setMessage((prev) => (prev === "" ? msg : prev));
        setEnabled((prev) =>
          prev === false && prev !== isEnabled ? isEnabled : prev
        );
        setType((prev) => (prev === "warning" ? t || "warning" : prev));

        setLoading(false);
      },
      (err) => {
        setStatus({ kind: "error", text: err?.message || "Failed to load" });
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const onUploadGuide = async () => {
    setStatus({ kind: "", text: "" });

    if (!selectedGuideFile) {
      setStatus({ kind: "error", text: "Please choose a PDF file first." });
      return;
    }

    const isPdf =
      selectedGuideFile.type === "application/pdf" ||
      selectedGuideFile.name.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      setStatus({ kind: "error", text: "Only PDF files are allowed." });
      return;
    }

    setUploadingGuide(true);
    setUploadPct(0);

    try {
      const newUrl = await uploadCheck2Guide({
        file: selectedGuideFile,
        updatedByEmail: dbUpdatedBy || "", // swap for userEmail if available
        onProgress: setUploadPct,
        overwriteLatest: true, // uploads to /check2/Guide7/Check Guide.pdf
      });

      // ✅ update UI immediately
      setGuideUrl(newUrl);

      setSelectedGuideFile(null);
      setStatus({ kind: "success", text: "Guide uploaded and link updated." });
    } catch (e) {
      setStatus({ kind: "error", text: e?.message || "Upload failed." });
    } finally {
      setUploadingGuide(false);
    }
  };

  const resetToDb = () => {
    setMessage(dbMessage);
    setEnabled(dbEnabled);
    setType(dbType || "warning");
    setStatus({ kind: "", text: "" });
  };

  const onSave = async () => {
    setStatus({ kind: "", text: "" });

    const msg = clean(message);
    if (enabled && !msg) {
      setStatus({
        kind: "error",
        text: "Please enter a message before enabling.",
      });
      return;
    }

    setSaving(true);
    try {
      await writeCheck2Announcement({
        announcement: msg,
        enabled,
        announcementType: type,
        // updatedByEmail: userEmail,
      });

      setStatus({ kind: "success", text: "Saved." });
    } catch (e) {
      setStatus({ kind: "error", text: e?.message || "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="c2AnnAdminPage">Loading…</div>;
  }

  return (
    <div className="c2AnnAdminPage">
      <div className="c2AnnAdminHeader">
        <div>
          <div className="Content-SemiBold">check² Service Announcement</div>
        </div>
      </div>
      {/* <div className="c2AnnFoot Content-Small">
          Current status: <b>{dbEnabled ? "Enabled" : "Disabled"}</b>, Type{" "}
                  <b>{dbType}</b>, Message {" "}<b>{clean(dbMessage)}</b>
        </div> */}
      <div className="Content-Small">
        Last updated by <b>{dbUpdatedBy}</b>, on <b>{dbUpdatedOn}</b>.
      </div>
      {/* {status.text && (
        <div className="c2AnnAdminStatus">
          <Banner
            type={status.kind === "error" ? "danger" : status.kind}
            message={status.text}
          />
        </div>
      )} */}

      <div className="c2AnnAdminGrid">
        <div className="c2AnnCard">
          <div className="c2AnnRow">
            <label className="Content">Enable</label>
            <label className="c2Switch">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span className="c2Slider" />
            </label>
          </div>

          <div className="c2AnnRow">
            <label className="Content">Type</label>
            <select
              className="c2AnnSelect"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="c2AnnRow c2AnnRowColumn">
            <label className="Content">Message</label>
            <textarea
              className="c2AnnTextarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type the announcement shown to users…"
              rows={5}
            />
          </div>
        </div>

        {/* Banner preview */}
        <div className="c2AnnCard">
          <div className="Content-SemiBold">Preview</div>
          <Banner type={type} message={clean(message)} />
        </div>
        {/* {enabled && clean(message) && (
        )} */}

        {/* <div className="c2AnnPopupMock">
            <div className="AML-Text-Title">Service Announcement</div>
            <div className="AML-Text-Body" style={{ marginTop: 8 }}>
              {clean(message) || "Your announcement will appear here…"}
            </div>
            <div style={{ marginTop: 12 }}>
              <ButtonGreen buttonText="Got it" onClick={() => {}} />
            </div>
          </div> */}
      </div>

      <div className="c2AnnAdminActions">
        {/* <ButtonGreen
          buttonText="Reset"
          onClick={resetToDb}
          disabled={!hasChanges || saving}
        /> */}
        <ButtonSmall
          buttonText={saving ? "Saving…" : "Save"}
          onClick={onSave}
          disabled={!hasChanges || saving}
        />
      </div>
      <div className="c2AnnCard">
        <div className="Content-SemiBold">check² Guide (PDF)</div>

        <div className="Content-Small" style={{ marginTop: 8 }}>
          Current guide:{" "}
          {guideUrl ? (
            <a
              href={guideUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="AML-Text-Input"
            >
              Open guide
            </a>
          ) : (
            <span>Not set</span>
          )}
        </div>

        {/* Optional: show the actual URL (useful for confirming it updated) */}
        {guideUrl && (
          <div style={{ marginTop: 12 }}>
            <ButtonSmall
              buttonText="View"
              onClick={() =>
                window.open(guideUrl, "_blank", "noopener,noreferrer")
              }
            />
          </div>
        )}

        <div style={{ height: 20 }}>
        
        </div>

        <div
          style={{
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setSelectedGuideFile(e.target.files?.[0] || null)}
            disabled={uploadingGuide}
          />

          <ButtonSmall
            buttonText={
              uploadingGuide ? `Uploading… ${uploadPct}%` : "Upload"
            }
            onClick={onUploadGuide}
            disabled={!selectedGuideFile || uploadingGuide}
          />

          {selectedGuideFile && (
            <div className="Content-Small">
              Selected: <b>{selectedGuideFile.name}</b>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
